import {Platform, Pressable, View} from 'react-native'

import {atoms as a, useTheme} from '#/alf'
import {useInteractionState} from '#/components/hooks/useInteractionState'
import {AddCapsule} from '#/components/icons/AddCapsule'
import {Text} from '#/components/Typography'
import {CAPSULE_ASPECT} from './types'

/**
 * The way into the capsule work, as the first cell of the capsule grid.
 *
 * It used to be a button on a line of its own above the grid, and a line above a
 * grid is not where a reader looks: the grid is. Somebody who opens a Capsules tab
 * is looking at cards, so the way to add one is one of them — the first one, where
 * the eye starts. It is also the only cell in the grid that is not somebody's work,
 * and drawing it in the grid's own proportions (the same 9:16 card the capsules
 * beside it use, with its own caption under it) is what says so without a word of
 * explanation.
 *
 * It is drawn as an *invitation* rather than a card: a dashed outline, the accent
 * colour, and no film, because there is nothing here to play. Every other cell is a
 * finished piece of work; this one is a space where work goes.
 *
 * What it does when pressed is the caller's, and the two callers are asking different
 * questions with it: a classroom's grid opens the *picker* (one of the capsules you
 * have made, or a new one for this room — see `AddCapsuleDialog`), while a creator's
 * own profile opens the studio, because there is nobody to take a capsule from but
 * yourself. The card is the same card either way, so the two read as one mechanism;
 * the words under it say which question it is asking, which is why they are props
 * rather than a constant.
 */
export function AddCapsuleCard({
  onPress,
  testID,
  label,
  caption,
  aspectRatio = CAPSULE_ASPECT,
}: {
  onPress: () => void
  /** The recipe that drives this grid names its own cells. */
  testID: string
  /** What the card says on itself: the act, in the caller's words. */
  label: string
  /** The line under it, in the place a capsule keeps its title and author. */
  caption: string
  /** The grid's proportions: the capsule card's, unless the caller says otherwise. */
  aspectRatio?: number
}) {
  const t = useTheme()
  const {
    state: pressed,
    onIn: onPressIn,
    onOut: onPressOut,
  } = useInteractionState()

  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityHint={caption}
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      style={[a.flex_col, Platform.OS === 'web' ? {cursor: 'pointer'} : null]}>
      <View
        style={[
          a.justify_center,
          a.align_center,
          a.rounded_md,
          a.gap_sm,
          a.p_md,
          {
            aspectRatio,
            borderWidth: 1.5,
            borderStyle: 'dashed',
            borderColor: t.palette.primary_500,
            /**
             * Filled, faintly, in both themes.
             *
             * A card drawn on nothing is a card that disappears in light mode,
             * where the page behind it is already the lightest thing on the
             * screen — the dashed outline and the accent are not enough to make a
             * slot in a grid read as one. `bg_contrast_25` is the app's own
             * whisper of a surface, and pressing it deepens to 50, which is the
             * only state this card has: every other cell in the grid dims its
             * film when pressed.
             */
            backgroundColor: pressed
              ? t.atoms.bg_contrast_50.backgroundColor
              : t.atoms.bg_contrast_25.backgroundColor,
          },
          Platform.OS === 'web' && a.w_full,
        ]}>
        {/*
          The mark for the act this card invites: a capsule with a plus in it, with
          the cards it joins showing behind it. Drawn in the accent colour, the one
          thing on this card that is not a whisper.
        */}
        <AddCapsule size="4xl" fill={t.palette.primary_500} />

        <Text
          style={[
            a.text_sm,
            a.font_semi_bold,
            a.text_center,
            {color: t.palette.primary_500},
          ]}>
          {label}
        </Text>
      </View>

      {/*
        The handful of words a card keeps under it, in the place a capsule keeps
        its title and author. It says what the card does rather than naming a
        second button, because there is only one thing to do here and the card
        already says it.
      */}
      <View style={[a.pr_xs, {paddingTop: 6, gap: 4}]}>
        <Text
          style={[a.text_sm, a.leading_tight, t.atoms.text_contrast_medium]}
          numberOfLines={2}>
          {caption}
        </Text>
      </View>
    </Pressable>
  )
}
